<script setup>
// Aviso de actualización + cuenta regresiva (Victor 2026-05-24, propuesta P4).
// Aparece cuando el vigilante de versión (App.vue) detecta un deploy nuevo.
// Flujo: overlay de aviso → "Entendido" inicia cuenta regresiva de 60s en un
// chip arriba-derecha (no bloquea para que puedan guardar) → al llegar a 0
// emite 'expired' y App.vue cierra la sesión (logout + recarga versión nueva).
import { ref, computed, onBeforeUnmount } from 'vue';

const emit = defineEmits(['expired']);

const TOTAL = 60;
const phase = ref('notice'); // 'notice' | 'counting'
const seconds = ref(TOTAL);
let timer = null;

const ringStyle = computed(() => ({
  '--p': `${(Math.max(seconds.value, 0) / TOTAL) * 100}%`,
}));
const urgent = computed(() => seconds.value <= 10);

function start() {
  phase.value = 'counting';
  seconds.value = TOTAL;
  timer = setInterval(() => {
    seconds.value -= 1;
    if (seconds.value <= 0) {
      clearInterval(timer);
      emit('expired');
    }
  }, 1000);
}

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <!-- Aviso overlay (no cuenta todavía) -->
  <div v-if="phase === 'notice'" class="dn-overlay">
    <div class="dn-seal">⟳</div>
    <div class="dn-label">Actualización en curso</div>
    <h2 class="dn-title">El sistema se reiniciará</h2>
    <p class="dn-text">
      Se está subiendo una actualización.
      <span class="dn-g">Guarda lo que estás haciendo.</span> Al pulsar
      <b>Entendido</b> comenzará una cuenta regresiva de
      <span class="dn-g">60 segundos</span>; al terminar, el sistema se
      reiniciará y se cerrará tu sesión.
    </p>
    <button class="dn-btn" @click="start">Entendido</button>
  </div>

  <!-- Chip cuenta regresiva arriba-derecha -->
  <div v-else class="dn-chip">
    <span class="dn-ring" :style="ringStyle">
      <span class="dn-num">{{ Math.max(seconds, 0) }}</span>
    </span>
    <span class="dn-chip-text">
      <b>Reinicio del sistema</b>
      <small>{{ urgent ? '¡guarda ya!' : 'guarda tu trabajo' }}</small>
    </span>
  </div>
</template>

<style scoped>
.dn-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 6%;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(6px);
  animation: dnFade 0.4s ease;
}
.dn-seal {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 42px;
  color: #1a1407;
  background: radial-gradient(circle at 30% 30%, #e3c98a, #c9a35a);
  box-shadow: 0 0 50px rgba(201, 163, 90, 0.45);
  animation: dnBeat 1.6s ease-in-out infinite;
}
.dn-label {
  font-size: 11px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #c9a35a;
  margin-top: 14px;
  font-weight: 800;
}
.dn-title {
  font-size: 30px;
  font-weight: 800;
  margin-top: 6px;
  letter-spacing: -0.5px;
  color: #fff;
}
.dn-text {
  margin-top: 18px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.82);
  max-width: 520px;
  line-height: 1.6;
}
.dn-text b {
  color: #fff;
}
.dn-g {
  color: #e3c98a;
  font-weight: 700;
}
.dn-btn {
  margin-top: 30px;
  padding: 14px 34px;
  border: 0;
  border-radius: 13px;
  cursor: pointer;
  background: linear-gradient(135deg, #e3c98a, #c9a35a);
  color: #1a1407;
  font-weight: 800;
  font-size: 15px;
  box-shadow: 0 12px 30px rgba(201, 163, 90, 0.3);
}
.dn-btn:active {
  transform: scale(0.97);
}

.dn-chip {
  position: fixed;
  top: 16px;
  right: 18px;
  z-index: 9000;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 16px 10px 11px;
  border-radius: 999px;
  background: rgba(12, 12, 13, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(201, 163, 90, 0.45);
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.55);
  animation: dnChipIn 0.45s ease;
}
.dn-ring {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: conic-gradient(#c9a35a var(--p, 100%), rgba(255, 255, 255, 0.12) 0);
}
.dn-ring::after {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #0c0c0d;
}
.dn-num {
  position: relative;
  z-index: 2;
  font-size: 13px;
  color: #e3c98a;
  font-weight: 800;
}
.dn-chip-text b {
  font-size: 13px;
  display: block;
  color: #fff;
}
.dn-chip-text small {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
}

@keyframes dnFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes dnBeat {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}
@keyframes dnChipIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
</style>
